/*
  Reels experience (sections/reels-experience.liquid)

  One product at a time, full screen. Each product shows its video (if any) and then
  the first photo of each colour, advancing automatically (photos every N ms, the video
  when it ends). Gestures:
    - swipe up / down     next / previous product
    - swipe left / right  next / previous photo (and stops the automatic advance)
    - press and hold      pause
    - pinch               zoom into the photo/video (springs back when released)
    - on the last photo, pulling further (a long swipe) loads the product's other
      photos, grouped by colour; a hint and a pull indicator guide the gesture
  Tapping a size adds that colour + size to the cart; the cart pill animates.
  Tapping the bottom bar opens the product page.
  After the last product comes an end screen (logo + link to the homepage).
  Bottom navigation: home (default collection), search, bag (cart drawer) and a menu.
  Every collection (menu, search, links inside opened pages) loads in place as a new reel;
  other pages and products open in a frame above the reel, keeping the bottom navigation.
  The search (at the top) shows product results live, below the field.

  Loading: only the photo on screen and the next one are requested; the previous and
  next products are built ahead of time (first photo / video poster only) so a swipe
  shows them instantly. Videos only start streaming when they are on screen.
*/
(function () {
  var SWIPE = 40;
  var ANIMATION_MS = 400;
  var PULL_PX = 180; // how far to pull past the last photo to load more
  // Full width on vertical screens (phones and portrait tablets); a phone-shaped column in landscape
  var IMAGE_WIDTHS = [540, 720, 900, 1200, 1600];
  // Very dense phone screens (3x) get the 900px photo instead of the 1200px one: about 30% less
  // to download, the same sharpness to the eye (same rule in sections/new-experience.liquid)
  var IMAGE_SIZES = '(orientation: landscape) 56vh, (min-resolution: 2.5dppx) 75vw, (-webkit-min-device-pixel-ratio: 2.5) 75vw, 100vw';
  // Videos up to 720p: sharp on a phone, about half the data of 1080p (assets/video-hls.js)
  var VIDEO_MAX_HEIGHT = 720;

  // Shopify CDN url (with width=720) -> url / srcset at other widths
  function sized(url, width) {
    return url.replace(/([?&])width=\d+/, '$1width=' + width);
  }
  function srcsetFor(url) {
    return IMAGE_WIDTHS.map(function (width) { return sized(url, width) + ' ' + width + 'w'; }).join(', ');
  }

  // "bordô" -> "Bordô", "azul claro" -> "Azul Claro" (Portuguese minor words stay lowercase)
  var MINOR_WORDS = ['a', 'o', 'as', 'os', 'e', 'de', 'da', 'do', 'das', 'dos', 'com', 'em'];
  function titleCase(text) {
    return String(text || '').toLowerCase().split(' ').map(function (word, index) {
      if (!word || (index > 0 && MINOR_WORDS.indexOf(word) !== -1)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    }).join(' ');
  }

  // "/collections/malhas" (optionally with a locale prefix) -> it's a single collection
  function isCollectionPath(pathname) {
    return /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^/]+\/?$/i.test(pathname);
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function Reels(root) {
    this.root = root;
    this.stage = root.querySelector('.reels__stage');
    this.toast = root.querySelector('[data-toast]');
    this.cartCount = root.querySelector('[data-cart-count]');
    this.cartTotal = root.querySelector('[data-cart-total]');
    this.cartLink = root.querySelector('[data-cart]');
    this.moneyFormat = root.dataset.moneyFormat || '{{amount_with_comma_separator}} €';
    this.photoMs = parseInt(root.dataset.photoMs, 10) || 1000;
    this.autoNext = root.dataset.autoNext === 'true';
    this.auto = true;
    this.paused = false;
    this.timer = null;

    var products = [];
    try {
      products = JSON.parse(root.querySelector('[data-reels-products]').textContent) || [];
    } catch (e) {
      products = [];
    }
    this.products = this.prepareProducts(products);
    this.readSourceData(root);

    this.cartItems = parseInt((this.cartCount && this.cartCount.textContent) || '0', 10) || 0;
    this.cartCents = parseInt((this.cartTotal && this.cartTotal.dataset.cents) || '0', 10) || 0;
    this.bindNav();
    // The first photo sent in the HTML (sections/new-experience.liquid) goes once the reel covers it
    var firstPhoto = root.querySelector('[data-reels-first]');
    if (firstPhoto) setTimeout(function () { firstPhoto.remove(); }, this.products.length ? 2500 : 0);
    if (!this.products.length) return;

    this.start();
    this.bind();
    // A collection asked for from the site's bottom bar (#c=...)
    if (this.pendingSource) this.loadSource(this.pendingSource);
    else this.trackList(false, '', true);
    // The first time: a short tutorial over the reel (assets/reels-tutorial.js, loaded only then)
    this.maybeTutorial(false);

    var self = this;
    var idle = window.requestIdleCallback || function (callback) { return setTimeout(callback, 600); };
    idle(function () {
      self.prepareNeighbours();
      self.warmAhead();
      // Fetch the HLS player early (only where it is needed) so the first video starts at once
      var hasVideo = self.products.some(function (product) { return product.video; });
      if (hasVideo && window.VideoHLS && window.VideoHLS.warmup) window.VideoHLS.warmup();
    });
  }

  // Which collection / search is shown (for the end screen) and the collections offered there
  Reels.prototype.readSourceData = function (root) {
    this.sourceTitle = root.dataset.sourceTitle || '';
    this.sourceUrl = root.dataset.sourceUrl || '';
    try {
      this.endCollections = JSON.parse(root.querySelector('[data-reels-end]').textContent) || [];
    } catch (e) {
      this.endCollections = [];
    }
  };

  // Adds the slide list (video + one photo per colour) to the raw product data
  Reels.prototype.prepareProducts = function (products) {
    products.forEach(function (product) {
      product.slides = [];
      if (product.video && (product.video.hls || product.video.mp4) && !product.soldOut) {
        // The video's colour (from its photo group); single-colour products use that colour
        var videoSlide = { type: 'video' };
        var videoColor = -1;
        product.colors.forEach(function (color, index) {
          if (color.name === product.video.color) videoColor = index;
        });
        if (videoColor === -1 && product.colors.length === 1) videoColor = 0;
        if (videoColor !== -1) videoSlide.color = videoColor;
        // If the video's colour is sold out it isn't loaded at all: start on the next colour
        var colourSoldOut = videoColor === -1 && product.video.color;
        if (!colourSoldOut) product.slides.push(videoSlide);
      }
      product.colors.forEach(function (color, index) {
        product.slides.push({ type: 'photo', color: index });
      });
    });
    return products.filter(function (product) {
      return product.slides.length > 0 && product.colors.length > 0;
    });
  };

  // Shows the first product of this.products (used at start and after loading a new reel)
  Reels.prototype.start = function () {
    this.views = {};
    this.index = 0;
    this.auto = true;
    this.root.classList.remove('is-end');
    this.current = this.viewFor(0);
    this.current.node.style.transform = 'translateY(0)';
    this.showSlide(0, 0);
    this.trackProduct(this.current);
    // Build the next product straight away so the first swipe is instant
    this.prepareNeighbours();
  };

  /* ---------- building ---------- */

  Reels.prototype.buildProduct = function (index) {
    var self = this;
    var product = this.products[index];
    var view = { index: index, product: product, slide: 0, slides: [], node: el('div', 'reels__product') };

    var media = el('div', 'reels__media');
    var progress = el('div', 'reels__progress');
    view.bars = [];
    media.appendChild(progress);

    view.media = media;
    view.progress = progress;
    product.slides.forEach(function (slide, slideIndex) {
      self.addSlideNode(view, slide, slideIndex);
    });

    view.sizes = el('div', 'reels__sizes');
    media.appendChild(view.sizes);

    if (product.soldOut) {
      media.appendChild(el('div', 'reels__soldout', this.root.dataset.soldOutText || ''));
    }

    view.hint = el('div', 'reels__hint');
    view.hint.setAttribute('aria-hidden', 'true');
    var arrow = el('span', 'reels__hint-arrow');
    arrow.innerHTML = '<svg width="16" height="16" viewBox="0 0 16 16"><path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>' + '<svg width="16" height="16" viewBox="0 0 16 16"><path d="M10 3L5 8l5 5" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    // While pulling, the pill fills up from the right; when full it says to let go
    view.hint.appendChild(el('span', 'reels__hint-fill'));
    view.hint.appendChild(arrow);
    view.hintText = view.hint.appendChild(el('span', 'reels__hint-text', this.root.dataset.moreHint || ''));
    media.appendChild(view.hint);


    // Details over the photo: the category (always visible, opens it as a reel), the title on one
    // line + "Detalhes" (full title, measurements, composition), then the colour row: current
    // colour, other colours and the price
    var details = el('div', 'reels__details');
    var data = this.root.dataset;
    if (product.category && product.category.url) {
      var category = el('button', 'reels__category');
      category.type = 'button';
      category.setAttribute('aria-label', (data.seeMoreText || 'Ver mais') + ' ' + titleCase(product.category.title));
      category.appendChild(el('span', null, titleCase(product.category.title)));
      category.insertAdjacentHTML('beforeend', '<svg width="10" height="10" viewBox="0 0 16 16" aria-hidden="true"><path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="2.2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>');
      category.addEventListener('click', function () {
        self.loadSource(product.category.url);
      });
      details.appendChild(category);
    }
    var head = el('div', 'reels__head');
    var title = el('a', 'reels__title', product.title);
    title.href = product.url;
    title.addEventListener('click', function (event) {
      event.preventDefault();
      self.openPage(product.url);
    });
    head.appendChild(title);
    var extra = el('div', 'reels__extra');
    extra.hidden = true;
    // Metafield text over several lines ("Altura:" / "58cm" / "Cava:" / "57cm") -> "Altura: 58cm, Cava: 57cm"
    var tidy = function (text) {
      var lines = String(text || '').split(/\r?\n/).map(function (line) { return line.trim(); }).filter(Boolean);
      var merged = [];
      lines.forEach(function (line) {
        var last = merged.length - 1;
        if (last >= 0 && /:$/.test(merged[last])) merged[last] += ' ' + line;
        else merged.push(line);
      });
      return merged.join(', ');
    };
    var addInfo = function (label, text) {
      text = tidy(text);
      if (!text) return;
      var line = el('p');
      line.appendChild(el('strong', null, label + ': '));
      line.appendChild(document.createTextNode(text));
      extra.appendChild(line);
    };
    addInfo(data.medidasLabel || 'Medidas', product.medidas);
    addInfo(data.composicaoLabel || 'Composição', product.composicao);

    // "Detalhes" (a pill with a chevron, right after the title) / "Fechar" while open
    var more = el('button', 'reels__more');
    more.type = 'button';
    more.setAttribute('aria-expanded', 'false');
    var moreLabel = more.appendChild(el('span', null, data.moreText || 'Detalhes'));
    more.insertAdjacentHTML('beforeend', '<svg width="10" height="10" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 6l5 5 5-5" stroke="currentColor" stroke-width="2.2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>');
    more.addEventListener('click', function () {
      var open = !details.classList.contains('is-open');
      details.classList.toggle('is-open', open);
      extra.hidden = !open;
      moreLabel.textContent = open ? (data.lessText || 'Fechar') : (data.moreText || 'Detalhes');
      more.setAttribute('aria-expanded', String(open));
      // The button moves below the extra text while open
      if (open) details.insertBefore(more, row);
      else head.appendChild(more);
      // Reading: hold the automatic advance while the details are open
      if (self.current === view) {
        if (open) self.pause();
        else self.resume();
      }
    });
    head.appendChild(more);
    details.appendChild(head);
    details.appendChild(extra);

    var row = el('div', 'reels__row');
    view.color = el('div', 'reels__color');
    view.others = el('div', 'reels__others');
    view.price = el('div', 'reels__price');
    row.appendChild(view.color);
    row.appendChild(view.others);
    row.appendChild(view.price);
    details.appendChild(row);
    media.appendChild(details);
    view.details = details;

    view.node.appendChild(media);
    view.node.style.transition = 'none';
    return view;
  };

  Reels.prototype.addSlideNode = function (view, slide, slideIndex) {
    var self = this;
    var product = view.product;
    var node = el('div', 'reels__slide');
    node.style.transition = 'none';
    node.style.transform = slideIndex === 0 ? 'translateX(0)' : 'translateX(100%)';
    if (slide.type === 'video') {
        var video = el('video');
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('muted', '');
        video.preload = 'none';
        if (product.video.poster) video.poster = product.video.poster;
        video.addEventListener('ended', function () {
          if (self.current === view && view.slide === slideIndex) self.advance();
        });
        video.addEventListener('error', function () {
          if (self.current === view && view.slide === slideIndex && self.auto) self.advance();
        });
        node.appendChild(video);
        view.video = video;
    } else {
      var image = slide.image || (slide.type === 'photo' ? product.colors[slide.color].image : null);
      if (image) {
        // The file is only requested by loadSlide(), right before it is needed
        var img = el('img');
        var colorName = slide.type === 'photo' ? product.colors[slide.color].name : '';
        img.alt = product.title + (colorName ? ' - ' + colorName : '');
        img.decoding = 'async';
        img.dataset.src = image;
        node.appendChild(img);
      }
    }
    view.media.insertBefore(node, view.sizes || null);
    view.slides.push(node);
    view.bars = view.bars || [];
    view.bars.push(view.progress.appendChild(el('span')));
  };

  Reels.prototype.renderDetails = function (view) {
    var self = this;
    var slide = view.product.slides[view.slide];
    var known = typeof slide.color === 'number';
    var color = view.product.colors[known ? slide.color : 0];

    // When the colour on screen isn't known (video/photo outside the colour groups) hide colour and sizes
    view.color.hidden = !known;
    view.others.hidden = !known;
    view.sizes.hidden = !known || !!view.product.soldOut;
    if (view.renderedColor === color) return;
    view.renderedColor = color;

    this.renderColorRow(view, color);

    var first = color.sizes.filter(function (size) { return size.available !== false; })[0] || color.sizes[0];
    view.price.textContent = '';
    if (first) {
      view.price.appendChild(document.createTextNode(first.price));
      if (first.compare) view.price.appendChild(el('del', null, first.compare));
    }

    view.sizes.textContent = '';
    color.sizes.forEach(function (size) {
      var button = el('button', 'reels__size', size.label);
      button.type = 'button';
      // Long labels (e.g. "OneSize") get a smaller font so they fit the circle
      var length = String(size.label).length;
      if (length >= 7) button.classList.add('is-longer');
      else if (length >= 5) button.classList.add('is-long');
      button.setAttribute('aria-label', view.product.title + ' ' + color.name + ' ' + size.label);
      if (size.available === false) {
        // Sold out: shown crossed out, not clickable
        button.classList.add('is-soldout');
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('aria-label', button.getAttribute('aria-label') + ' - ' + (self.root.dataset.soldOutText || ''));
      } else {
        button.addEventListener('click', function () {
          self.addToCart(size, button);
        });
      }
      view.sizes.appendChild(button);
    });
  };

  function swatchNode(color) {
    var swatch = el('span', 'reels__swatch');
    if (color.swatch && color.swatch.img) swatch.style.backgroundImage = 'url("' + color.swatch.img + '")';
    else if (color.swatch && color.swatch.color) swatch.style.backgroundColor = color.swatch.color;
    return swatch;
  }

  // Current colour (swatch + name) followed by the other colours; tapping one jumps to it
  Reels.prototype.renderColorRow = function (view, color) {
    var self = this;
    var product = view.product;
    view.color.textContent = '';
    view.color.appendChild(swatchNode(color));
    view.color.appendChild(el('span', 'reels__color-name', titleCase(color.name)));

    var others = view.others;
    others.textContent = '';
    product.colors.forEach(function (other, colorIndex) {
      if (other === color) return;
      var button = el('button', 'reels__other');
      button.type = 'button';
      button.setAttribute('aria-label', titleCase(other.name));
      button.appendChild(swatchNode(other));
      button.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        self.jumpToColor(view, colorIndex);
      });
      others.appendChild(button);
    });
  };

  Reels.prototype.jumpToColor = function (view, colorIndex) {
    if (view !== this.current) return;
    var target = -1;
    view.product.slides.forEach(function (slide, index) {
      if (target === -1 && slide.type === 'photo' && slide.color === colorIndex) target = index;
    });
    if (target === -1 || target === view.slide) return;
    // Choosing a colour is a manual action: stop the automatic advance
    this.auto = false;
    this.clearTimer();
    if (view.video) view.video.loop = true;
    this.showSlide(target, target > view.slide ? 1 : -1);
  };

  Reels.prototype.loadSlide = function (view, slideIndex, priority) {
    var node = view.slides[slideIndex];
    if (!node) return;
    var img = node.querySelector('img[data-src]');
    if (img) {
      if (priority) img.setAttribute('fetchpriority', 'high');
      img.sizes = IMAGE_SIZES;
      img.srcset = srcsetFor(img.dataset.src);
      img.src = img.dataset.src;
      img.removeAttribute('data-src');
    }
  };

  // Returns the (cached) view of a product, adding it to the stage off screen
  // End screen after the last product: "you've seen all of <collection>" + other collections
  Reels.prototype.buildEnd = function (index) {
    var self = this;
    var data = this.root.dataset;
    var view = { index: index, isEnd: true, slides: [], slide: 0, node: el('div', 'reels__product reels__end') };
    var inner = el('div', 'reels__end-inner');
    if (data.endLogo) {
      var logo = el('img', 'reels__end-logo');
      logo.src = data.endLogo;
      if (data.endLogoSrcset) logo.srcset = data.endLogoSrcset;
      logo.sizes = '150px';
      logo.alt = data.endLogoAlt || '';
      inner.appendChild(logo);
    }
    // "Novidades em Roupa de Mulher - A tua coleção começa aqui" -> "Novidades em Roupa de Mulher"
    var source = this.sourceTitle ? titleCase(this.sourceTitle.split(' - ')[0].trim()) : '';
    inner.appendChild(el('p', 'reels__end-title', (data.endTitle || '').replace('[colecao]', source).replace(/\s+/g, ' ').trim()));
    inner.appendChild(el('p', 'reels__end-subtitle', data.endSubtitle || ''));

    var grid = el('div', 'reels__end-grid');
    (this.endCollections || []).forEach(function (collection) {
      if (collection.url === self.sourceUrl) return;
      var tile = el('a', 'reels__end-tile');
      tile.href = collection.url;
      if (collection.image) {
        var img = el('img');
        img.src = collection.image;
        img.alt = '';
        img.loading = 'lazy';
        tile.appendChild(img);
      }
      tile.appendChild(el('span', 'reels__end-tile-title', titleCase(collection.title)));
      tile.addEventListener('click', function (event) {
        event.preventDefault();
        self.loadSource(collection.url);
      });
      grid.appendChild(tile);
    });
    inner.appendChild(grid);

    // The mosaic scrolls natively, so the reel's swipe handling ignores it: a downward swipe
    // that starts with the list at the top goes back to the last product
    var startY = null;
    var startX = 0;
    var startedAtTop = false;
    var begin = function (x, y) {
      startX = x;
      startY = y;
      startedAtTop = inner.scrollTop <= 0;
    };
    var end = function (x, y) {
      if (startY === null) return;
      var dy = y - startY;
      var dx = x - startX;
      startY = null;
      if (startedAtTop && inner.scrollTop <= 0 && dy > SWIPE && Math.abs(dy) > Math.abs(dx) && self.current === view) {
        self.goProduct(-1);
      }
    };
    inner.addEventListener('touchstart', function (event) {
      begin(event.touches[0].clientX, event.touches[0].clientY);
    }, { passive: true });
    inner.addEventListener('touchend', function (event) {
      var touch = event.changedTouches[0];
      end(touch.clientX, touch.clientY);
    });
    inner.addEventListener('mousedown', function (event) { begin(event.clientX, event.clientY); });
    inner.addEventListener('mouseup', function (event) { end(event.clientX, event.clientY); });
    view.node.appendChild(inner);
    view.node.style.transition = 'none';
    return view;
  };

  Reels.prototype.viewFor = function (index) {
    var view = this.views[index];
    if (!view && index === this.products.length) {
      view = this.views[index] = this.buildEnd(index);
      view.node.style.transform = 'translateY(' + (index > this.index ? '100%' : '-100%') + ')';
      this.stage.appendChild(view.node);
    }
    if (!view) {
      view = this.views[index] = this.buildProduct(index);
      view.node.style.transform = 'translateY(' + (index > this.index ? '100%' : '-100%') + ')';
      this.stage.appendChild(view.node);
      this.loadSlide(view, 0, index === this.index);
      if (view.video && view.product.video.poster) view.video.poster = view.product.video.poster;
    }
    return view;
  };

  // Keep the previous and next products ready; drop the rest (and their videos)
  Reels.prototype.prepareNeighbours = function () {
    var self = this;
    [this.index + 1, this.index - 1].forEach(function (index) {
      if (index >= 0 && index <= self.products.length) {
        var view = self.viewFor(index);
        if (view !== self.current) {
          view.node.style.transition = 'none';
          view.node.style.transform = 'translateY(' + (index > self.index ? '100%' : '-100%') + ')';
        }
      }
    });
    Object.keys(this.views).forEach(function (key) {
      var index = parseInt(key, 10);
      if (Math.abs(index - self.index) > 1) {
        self.destroyView(self.views[key]);
        delete self.views[key];
      }
    });
  };

  Reels.prototype.destroyView = function (view) {
    if (view.video) {
      if (view.video._hls) view.video._hls.destroy();
      view.video.removeAttribute('src');
      view.video.load();
    }
    view.node.remove();
  };

  /* ---------- slides ---------- */

  Reels.prototype.showSlide = function (slideIndex, direction) {
    var view = this.current;
    if (view.isEnd) return;
    var previous = view.slide;
    view.slide = slideIndex;

    view.slides.forEach(function (node, index) {
      if (direction !== 0 && (index === previous || index === slideIndex)) {
        node.style.transition = '';
      } else {
        node.style.transition = 'none';
      }
      // Upcoming photos wait on the right and slide in leftwards; past ones leave to the left
      if (index === slideIndex) node.style.transform = 'translateX(0)';
      else if (index < slideIndex) node.style.transform = 'translateX(-100%)';
      else node.style.transform = 'translateX(100%)';
    });
    view.bars.forEach(function (bar, index) {
      bar.classList.toggle('is-active', index === slideIndex);
    });

    if (view.video && previous !== slideIndex && view.product.slides[previous].type === 'video') {
      view.video.pause();
    }

    this.loadSlide(view, slideIndex, true);
    this.loadSlide(view, slideIndex + 1, false);
    this.renderDetails(view);
    this.updateHint(view);
    this.clearTimer();

    var slide = view.product.slides[slideIndex];
    if (slide.type === 'video') {
      this.playVideo(view);
    } else if (this.auto) {
      this.startTimer(this.photoMs);
    }
  };

  Reels.prototype.playVideo = function (view) {
    var self = this;
    var video = view.video;
    video.loop = !this.auto;
    if (!view.videoReady) {
      view.videoReady = true;
      video.preload = 'auto';
      var source = view.product.video;
      var useMp4 = function () {
        if (source.mp4) video.src = source.mp4;
      };
      var attach = window.VideoHLS && source.hls ? window.VideoHLS.attach(video, source.hls, { maxHeight: VIDEO_MAX_HEIGHT }) : Promise.resolve(false);
      attach
        .then(function (usedHls) {
          if (!usedHls) useMp4();
        })
        .catch(useMp4)
        .then(function () {
          if (self.current === view && !self.paused) self.safePlay(video);
        });
    } else {
      try { video.currentTime = 0; } catch (e) {}
      if (!this.paused) this.safePlay(video);
    }
  };

  Reels.prototype.safePlay = function (video) {
    var self = this;
    var promise = video.play();
    if (promise && promise.catch) {
      promise.catch(function () {
        // Autoplay refused: move on to the photos instead of getting stuck
        if (self.auto && self.current && self.current.video === video) self.advance();
      });
    }
  };

  Reels.prototype.advance = function () {
    var view = this.current;
    if (view.slide < view.slides.length - 1) {
      this.showSlide(view.slide + 1, 1);
    } else if (this.auto && this.autoNext && view.index < this.products.length) {
      this.goProduct(1);
    }
  };

  Reels.prototype.manualSlide = function (direction) {
    if (this.current.isEnd) return;
    this.auto = false;
    this.clearTimer();
    if (this.current.video) this.current.video.loop = true;
    var view = this.current;
    var target = view.slide + direction;
    if (target >= view.slides.length) {
      // Past the last photo: bounce and nudge the "swipe for more" hint
      this.bounce(view);
      this.nudgeHint(view);
      return;
    }
    if (target < 0) {
      this.bounce(view, true);
      return;
    }
    this.showSlide(target, direction);
  };

  Reels.prototype.canLoadMore = function (view) {
    var state = view.product.morePhotos;
    return view.product.more && state !== 'done' && state !== 'loading';
  };

  Reels.prototype.updateHint = function (view) {
    if (!view.hint) return;
    var show = view.slide === view.slides.length - 1 && this.canLoadMore(view);
    view.hint.classList.toggle('is-visible', show);
    if (!show) view.hint.classList.remove('is-loading');
  };

  Reels.prototype.nudgeHint = function (view) {
    if (!view.hint || !view.hint.classList.contains('is-visible')) return;
    view.hint.classList.remove('is-nudge');
    void view.hint.offsetWidth;
    view.hint.classList.add('is-nudge');
  };

  // Live feedback while pulling past the last photo (distance in px, 0 = released)
  Reels.prototype.setPull = function (view, distance) {
    var node = view.slides[view.slide];
    var progress = Math.min(1, distance / PULL_PX);
    if (distance > 0) {
      node.style.transition = 'none';
      node.style.transform = 'translateX(' + (-distance * 0.35) + 'px)';
      var ready = progress >= 1;
      view.hint.style.setProperty('--p', progress);
      view.hint.classList.add('is-pulling');
      if (ready !== view.hint.classList.contains('is-ready')) {
        view.hint.classList.toggle('is-ready', ready);
        view.hintText.textContent = ready ? this.root.dataset.moreRelease || '' : this.root.dataset.moreHint || '';
      }
    } else {
      node.style.transition = '';
      node.style.transform = 'translateX(0)';
      view.hint.classList.remove('is-pulling', 'is-ready');
      view.hint.style.setProperty('--p', 0);
      view.hintText.textContent = this.root.dataset.moreHint || '';
    }
  };

  // Rebuilds a product's view after its slides changed (keeps its place on the stage)
  Reels.prototype.rebuildView = function (view) {
    var fresh = this.buildProduct(view.index);
    fresh.node.style.transform = view.node.style.transform;
    this.stage.insertBefore(fresh.node, view.node);
    this.loadSlide(fresh, 0, false);
    this.destroyView(view);
    this.views[view.index] = fresh;
    if (this.current === view) this.current = fresh;
    return fresh;
  };

  Reels.prototype.bounce = function (view, fromStart) {
    var node = view.slides[view.slide];
    if (!node || !node.animate) return;
    var offset = fromStart ? '24px' : '-24px';
    node.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(' + offset + ')' }, { transform: 'translateX(0)' }],
      { duration: 320, easing: 'ease-out' }
    );
  };

  // Fetches the product's other photos (only when the customer insists) and appends them
  Reels.prototype.loadMorePhotos = function (view) {
    var self = this;
    var product = view.product;
    if (product.morePhotos === 'loading' || product.morePhotos === 'done') return;
    product.morePhotos = 'loading';
    if (view.hint) view.hint.classList.add('is-loading');

    // Same photo can come as //shop-domain/cdn/shop/files/x.webp or cdn.shopify.com/.../files/x.webp:
    // compare by file name only
    var path = function (url) {
      return (url || '').split('?')[0].split('/').pop();
    };
    var shown = {};
    product.colors.forEach(function (color) {
      if (color.image) shown[path(color.image)] = true;
    });

    fetch(product.url.split('?')[0] + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        product.morePhotos = 'done';

        // Photos are grouped by colour: each colour's variants point at the first photo of
        // its group, and the photos that follow (until the next colour) belong to it.
        var colorIndexByName = {};
        product.colors.forEach(function (color, index) { colorIndexByName[color.name] = index; });
        var colorOption = -1;
        (data.options || []).forEach(function (option, index) {
          var values = option.values || [];
          if (colorOption === -1 && product.colors.some(function (color) { return values.indexOf(color.name) !== -1; })) {
            colorOption = index;
          }
        });
        var starts = {};
        (data.variants || []).forEach(function (variant) {
          var name = colorOption === -1 ? '' : variant.options[colorOption];
          var position = variant.featured_media && variant.featured_media.position;
          if (!position) return;
          if (!(name in starts) || position < starts[name]) starts[name] = position;
        });
        var groups = Object.keys(starts)
          .map(function (name) { return { name: name, start: starts[name] }; })
          .sort(function (a, b) { return a.start - b.start; });
        var colorForPosition = function (position) {
          var name = null;
          groups.forEach(function (group) { if (group.start <= position) name = group.name; });
          if (name === null && colorOption === -1) name = '';
          return name;
        };

        var extras = [];
        (data.media || []).forEach(function (media) {
          if (media.media_type !== 'image' || !media.src) return;
          var src = media.src.indexOf('//') === 0 ? 'https:' + media.src : media.src;
          if (shown[path(src)]) return;
          var name = colorForPosition(media.position);
          // Photos of a sold-out colour are skipped (that colour can't be added to the cart)
          if (name !== null && !(name in colorIndexByName)) return;
          shown[path(src)] = true;
          var slide = { image: src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=720' };
          if (name !== null) {
            slide.type = 'photo';
            slide.color = colorIndexByName[name];
          } else {
            slide.type = 'extra';
          }
          extras.push(slide);
        });

        if (!extras.length) {
          product.more = false;
          self.updateHint(view);
          return;
        }

        // Regroup: video first, then each colour's photo followed by the rest of its set
        var oldSlides = product.slides;
        var standing = oldSlides[view.slide];
        var grouped = oldSlides.filter(function (slide) { return slide.type === 'video'; });
        product.colors.forEach(function (color, colorIndex) {
          oldSlides.concat(extras).forEach(function (slide) {
            if (slide.type === 'photo' && slide.color === colorIndex) grouped.push(slide);
          });
        });
        extras.forEach(function (slide) { if (slide.type === 'extra') grouped.push(slide); });
        product.slides = grouped;

        var isCurrent = self.current === view;
        var fresh = self.rebuildView(view);
        if (!isCurrent) return;

        // Loading more is a manual action: stay on this product's photos
        self.auto = false;
        var from = grouped.indexOf(standing);
        fresh.slide = from;
        self.showSlide(from, 0);
        requestAnimationFrame(function () {
          if (self.current === fresh && from + 1 < fresh.slides.length) self.showSlide(from + 1, 1);
        });
      })
      .catch(function () {
        product.morePhotos = null;
        self.updateHint(view);
      });
  };

  /* ---------- analytics (window.ReelsTrack, assets/reels-bag.js) ---------- */

  // The product as Google / Meta / Klaviyo see it: the colour on screen, its first size in stock
  Reels.prototype.trackSize = function (product, colorIndex) {
    var color = product.colors[colorIndex || 0] || product.colors[0] || { sizes: [] };
    var sizes = color.sizes || [];
    var size = sizes.filter(function (item) { return item.available; })[0] || sizes[0] || {};
    return {
      id: size.id,
      sku: size.sku,
      cents: size.cents,
      variantTitle: [color.name, size.label].filter(function (part) { return part && part !== '+'; }).join(' / ')
    };
  };

  // A product counts as seen after a second on screen (fast swipes past it don't), once per reel
  Reels.prototype.trackProduct = function (view) {
    var self = this;
    if (!window.ReelsTrack || !view || view.isEnd || !view.product) return;
    clearTimeout(this.trackTimer);
    this.trackTimer = setTimeout(function () {
      // Not while the tutorial moves the reel by itself
      if (self.current !== view || self.tutorial) return;
      var product = view.product;
      self.trackedProducts = self.trackedProducts || {};
      if (self.trackedProducts[product.id]) return;
      self.trackedProducts[product.id] = true;
      var slide = view.slides[view.slide] || product.slides[0] || {};
      var size = self.trackSize(product, slide.color);
      var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
      var value = (size.cents || 0) / 100;
      var image = (product.colors[slide.color || 0] || product.colors[0] || {}).image;
      image = image && (image.url || image);
      if (typeof image === 'string' && image.indexOf('//') === 0) image = 'https:' + image;
      window.ReelsTrack.push({
        google: ['view_item', {
          currency: currency,
          value: value,
          item_list_name: self.sourceTitle || '',
          items: [window.ReelsTrack.googleItem(product, size, view.index)]
        }],
        meta: ['ViewContent', {
          content_ids: [String(product.id)],
          content_type: 'product_group',
          content_name: product.title,
          content_category: product.type || '',
          value: value,
          currency: currency
        }],
        klaviyo: ['track', 'Viewed Product', {
          ProductName: product.title,
          ProductID: product.id,
          SKU: size.sku || '',
          Categories: product.type ? [product.type] : [],
          ImageURL: typeof image === 'string' ? image : '',
          URL: window.ReelsTrack.url(product.url),
          Brand: product.vendor || '',
          Price: value
        }]
      });
    }, 1000);
  };

  // A reel (collection or search) shown: a virtual page view of that collection and its list
  Reels.prototype.trackList = function (isSearch, query, initial) {
    if (!window.ReelsTrack) return;
    var self = this;
    this.trackedProducts = {};
    var title = this.sourceTitle || '';
    var items = this.products.slice(0, 20).map(function (product, index) {
      return window.ReelsTrack.googleItem(product, self.trackSize(product, 0), index);
    });
    if (isSearch) {
      window.ReelsTrack.push({
        analyticsOnly: true,
        google: ['view_item_list', { item_list_id: 'search', item_list_name: 'Pesquisa: ' + (query || title), items: items }]
      });
      return;
    }
    // (not for the first reel: Shopify already counted the page itself)
    if (this.sourceUrl && !initial) {
      window.ReelsTrack.push({
        analyticsOnly: true,
        google: ['page_view', {
          page_location: window.ReelsTrack.url(this.sourceUrl),
          page_title: title + ' | New Experience'
        }]
      });
    }
    window.ReelsTrack.push({
      google: ['view_item_list', { item_list_id: this.sourceUrl || 'reels', item_list_name: title, items: items }]
    });
  };

  /* ---------- tutorial ---------- */

  // First visit (or "Ver o tutorial" in the menu): loads assets/reels-tutorial.js and runs it over
  // the reel. It waits for the first product to be on screen and for any open panel to close.
  Reels.prototype.maybeTutorial = function (force) {
    var self = this;
    var src = this.root.dataset.tutorialScript;
    if (!src || this.tutorial || (window.Shopify && window.Shopify.designMode)) return;
    if (!force) {
      try {
        if (localStorage.getItem('reels-tutorial') === 'done') return;
      } catch (e) {
        return;
      }
    }
    var tries = 0;
    var begin = function () {
      if (self.tutorial) return;
      // Not over an open panel, the end screen, or a collection still loading (it would replace
      // the products under the tutorial, e.g. the invite's link to "Novidades")
      var busy = self.root.querySelector('.reels__panel:not([hidden]), .reels__exit:not([hidden])') || !self.current || self.current.isEnd || self.loadingSource;
      if (busy && !force) {
        if (++tries < 60) setTimeout(begin, 1000);
        return;
      }
      self.loadTutorial().then(function () {
        if (!window.ReelsTutorial || self.tutorial) return;
        if (window.ReelsTrack) window.ReelsTrack.push({ analyticsOnly: true, google: ['tutorial_begin', { trigger: force ? 'menu' : 'first_visit' }] });
        self.tutorial = window.ReelsTutorial.start(self, {
          onEnd: function (completed, step) {
            self.tutorial = null;
            try { localStorage.setItem('reels-tutorial', 'done'); } catch (e) {}
            if (window.ReelsTrack) window.ReelsTrack.push({ analyticsOnly: true, google: [completed ? 'tutorial_complete' : 'tutorial_skip', { step: step }] });
          }
        });
      }).catch(function () {});
    };
    // First visit: once the first photo has been seen for a moment
    setTimeout(begin, force ? 0 : 2200);
  };

  Reels.prototype.loadTutorial = function () {
    var src = this.root.dataset.tutorialScript;
    if (window.ReelsTutorial) return Promise.resolve();
    if (!this.tutorialScript) {
      this.tutorialScript = new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    return this.tutorialScript;
  };

  /* ---------- products ---------- */

  Reels.prototype.goProduct = function (direction) {
    var target = this.index + direction;
    // products.length is the end screen
    if (target < 0 || target > this.products.length) return;
    // A swipe during the previous animation finishes it at once instead of being ignored
    if (this.animating) this.finishAnimation();
    this.animating = true;
    this.clearTimer();

    var old = this.current;
    if (old.video) old.video.pause();

    var next = this.viewFor(target);
    next.node.style.transition = 'none';
    next.node.style.transform = 'translateY(' + (direction > 0 ? '100%' : '-100%') + ')';
    void next.node.offsetHeight;
    next.node.style.transition = '';
    old.node.style.transition = '';
    next.node.style.transform = 'translateY(0)';
    old.node.style.transform = 'translateY(' + (direction > 0 ? '-100%' : '100%') + ')';

    this.index = target;
    this.current = next;
    this.root.classList.toggle('is-end', !!next.isEnd);
    next.slide = 0;
    this.showSlide(0, 0);
    this.trackProduct(next);

    // Start loading the product after this one right now (not after the animation)
    var ahead = target + direction;
    if (ahead >= 0 && ahead <= this.products.length && !this.views[ahead]) this.viewFor(ahead);
    this.preloadProduct(ahead + direction);

    var self = this;
    this.finishAnimation = function () {
      clearTimeout(self.animationTimer);
      self.finishAnimation = function () {};
      // The product we left stays ready (paused, rewound) in case the customer swipes back
      if (old.video) {
        try { old.video.currentTime = 0; } catch (e) {}
      }
      old.node.style.transition = 'none';
      self.animating = false;
      self.prepareNeighbours();
      self.warmAhead();
    };
    this.animationTimer = setTimeout(function () { self.finishAnimation(); }, ANIMATION_MS);
  };

  // Once a product is on screen (never during the page's first load): the next product's second
  // photo (its next colour) and the first photo of the product after it, so swipes find them ready
  Reels.prototype.warmAhead = function () {
    var self = this;
    // When the browser is free, or after 0.8 s at the latest (some pages are never "free")
    var later = window.requestIdleCallback
      ? function (callback) { return window.requestIdleCallback(callback, { timeout: 800 }); }
      : function (callback) { return setTimeout(callback, 300); };
    later(function () {
      var next = self.views[self.index + 1];
      if (next && !next.isEnd) self.loadSlide(next, 1, false);
      self.preloadProduct(self.index + 2);
    });
  };

  // Warms the browser cache with a product's first image (two products ahead)
  Reels.prototype.preloadProduct = function (index) {
    var product = this.products[index];
    if (!product || this.views[index]) return;
    var first = product.slides[0];
    var url = first && first.type === 'video' ? product.video.poster : first && product.colors[first.color] && product.colors[first.color].image;
    if (!url) return;
    var img = new Image();
    img.sizes = IMAGE_SIZES;
    if (first.type !== 'video') img.srcset = srcsetFor(url);
    img.src = url;
  };

  /* ---------- timer / pause ---------- */

  Reels.prototype.startTimer = function (ms) {
    var self = this;
    this.clearTimer();
    this.timer = { remaining: ms, start: Date.now(), id: null };
    if (!this.paused) {
      this.timer.id = setTimeout(function () {
        self.timer = null;
        self.advance();
      }, ms);
    }
  };

  Reels.prototype.clearTimer = function () {
    if (this.timer && this.timer.id) clearTimeout(this.timer.id);
    this.timer = null;
  };

  Reels.prototype.pause = function () {
    if (this.paused) return;
    this.paused = true;
    if (this.timer && this.timer.id) {
      clearTimeout(this.timer.id);
      this.timer.id = null;
      this.timer.remaining -= Date.now() - this.timer.start;
    }
    if (this.current.video) this.current.video.pause();
  };

  Reels.prototype.resume = function () {
    // The tutorial holds the reel still (coming back to the tab, closing panels... don't restart it)
    if (!this.paused || this.holdForTutorial) return;
    this.paused = false;
    var self = this;
    if (this.timer) {
      this.timer.start = Date.now();
      this.timer.id = setTimeout(function () {
        self.timer = null;
        self.advance();
      }, Math.max(0, this.timer.remaining));
    }
    if (this.current.isEnd) return;
    var slide = this.current.product.slides[this.current.slide];
    if (slide.type === 'video' && this.current.video) this.safePlay(this.current.video);
  };

  /* ---------- cart ---------- */

  // Space taken by the cart pill, so the top hint sits between the close button and the cart
  Reels.prototype.updateCartSpace = function () {
    // The cart lives in the bottom navigation: the top hint only has to clear the close button
    this.root.style.setProperty('--reels-cart-space', '56px');
  };

  Reels.prototype.renderCart = function (animate) {
    if (this.cartCount) {
      this.cartCount.textContent = this.cartItems;
      this.cartCount.hidden = this.cartItems === 0;
    }
    if (this.cartLink && animate) {
      // Restart the "added" animation on every add
      this.cartLink.classList.remove('is-added');
      void this.cartLink.offsetWidth;
      this.cartLink.classList.add('is-added');
    }
  };

  // Optimistic add to cart: the pill updates instantly, the request runs in the background
  // and the real cart totals replace the estimate when it answers (or it is undone on error)
  Reels.prototype.addToCart = function (size, button) {
    var self = this;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var cents = size.cents || 0;

    // Taps still waiting for an answer are kept on top of the server totals
    this.pendingItems = (this.pendingItems || 0) + 1;
    this.pendingCents = (this.pendingCents || 0) + cents;
    this.cartItems += 1;
    this.cartCents += cents;
    this.renderCart(true);
    var settle = function () {
      self.pendingItems -= 1;
      self.pendingCents -= cents;
    };
    if (navigator.vibrate) navigator.vibrate(15);

    // Requests run one after the other: simultaneous /cart/add.js calls overwrite each other
    this.cartQueue = (this.cartQueue || Promise.resolve()).then(function () {
      return fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: size.id, quantity: 1 }] })
      })
      .then(function (response) {
        if (!response.ok) throw new Error('add failed');
        return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        settle();
        self.cartItems = cart.item_count + self.pendingItems;
        self.cartCents = cart.total_price + self.pendingCents;
        self.renderCart(false);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
      })
      .catch(function () {
        settle();
        self.cartItems = Math.max(0, self.cartItems - 1);
        self.cartCents = Math.max(0, self.cartCents - cents);
        self.renderCart(false);
        self.showToast(self.root.dataset.errorText, true);
      });
    });
  };

  Reels.prototype.showToast = function (text, isError) {
    var toast = this.toast;
    toast.textContent = text;
    toast.classList.toggle('is-error', !!isError);
    toast.hidden = false;
    clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(function () {
      toast.hidden = true;
    }, 1800);
  };

  /* ---------- input ---------- */

  Reels.prototype.bind = function () {
    var self = this;
    if (this.bound) return;
    this.bound = true;
    var start = null;

    var pulling = null;

    // ---- Pinch to zoom (two fingers). Like Instagram: zooms while pinching, springs back on release
    var pointers = new Map();
    var zoom = null;
    var distance = function (a, b) { return Math.hypot(a.x - b.x, a.y - b.y); };
    var midpoint = function (a, b) { return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; };

    var startZoom = function () {
      var view = self.current;
      if (view.isEnd) return;
      var node = view.slides[view.slide];
      var target = node && node.querySelector('img, video');
      if (!target) return;
      var points = Array.from(pointers.values());
      var rect = target.getBoundingClientRect();
      var mid = midpoint(points[0], points[1]);
      // Cancel any swipe / pull in progress: two fingers mean zoom
      if (pulling) { self.setPull(pulling, 0); pulling = null; }
      start = null;
      zoom = { target: target, startDistance: distance(points[0], points[1]) || 1, startMid: mid };
      target.style.transition = 'none';
      target.style.transformOrigin = (mid.x - rect.left) + 'px ' + (mid.y - rect.top) + 'px';
      self.pause();
    };

    var endZoom = function () {
      var target = zoom.target;
      zoom = null;
      target.style.transition = 'transform 250ms ease';
      target.style.transform = '';
      setTimeout(function () { target.style.transition = ''; }, 260);
      self.resume();
    };

    this.root.addEventListener('pointerdown', function (event) {
      if (event.target.closest('.reels__nav, .reels__panel, .reels__end-grid, .reels__related, .reels__exit')) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (pointers.size === 2 && !zoom) {
        startZoom();
        return;
      }
      if (zoom || pointers.size > 1) return;
      // Buttons and links keep their tap; the end-screen link still lets the customer swipe
      if (event.target.closest('button, a:not(.reels__end-link)')) return;
      start = { x: event.clientX, y: event.clientY, id: event.pointerId };
      self.pause();
    });

    this.root.addEventListener('pointermove', function (event) {
      if (!pointers.has(event.pointerId)) return;
      pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (!zoom || pointers.size < 2) return;
      var points = Array.from(pointers.values());
      var scale = Math.min(4, Math.max(1, distance(points[0], points[1]) / zoom.startDistance));
      var mid = midpoint(points[0], points[1]);
      zoom.target.style.transform = 'translate(' + (mid.x - zoom.startMid.x) + 'px, ' + (mid.y - zoom.startMid.y) + 'px) scale(' + scale + ')';
    });

    var releasePointer = function (event) {
      pointers.delete(event.pointerId);
      if (zoom && pointers.size < 2) endZoom();
    };
    this.root.addEventListener('pointerup', releasePointer);
    this.root.addEventListener('pointercancel', releasePointer);

    // On the last photo, a leftward drag becomes a "pull for more photos"
    this.root.addEventListener('pointermove', function (event) {
      if (!start || event.pointerId !== start.id) return;
      var view = self.current;
      var dx = event.clientX - start.x;
      var dy = event.clientY - start.y;
      if (!pulling) {
        if (dx < -20 && Math.abs(dx) > Math.abs(dy) && view.slide === view.slides.length - 1 && self.canLoadMore(view)) {
          pulling = view;
        } else {
          return;
        }
      }
      self.setPull(pulling, Math.max(0, -dx));
    });

    var finish = function (event, cancelled) {
      if (!start || event.pointerId !== start.id) return;
      var dx = event.clientX - start.x;
      var dy = event.clientY - start.y;
      start = null;
      self.resume();

      if (pulling) {
        var view = pulling;
        pulling = null;
        self.setPull(view, 0);
        self.auto = false;
        self.clearTimer();
        if (!cancelled && -dx >= PULL_PX) {
          view.hint.classList.add('is-loading');
          self.loadMorePhotos(view);
        } else {
          self.nudgeHint(view);
        }
        return;
      }
      if (cancelled) return;
      if (Math.abs(dy) > SWIPE && Math.abs(dy) > Math.abs(dx)) {
        self.goProduct(dy < 0 ? 1 : -1);
      } else if (Math.abs(dx) > SWIPE) {
        // Swipe right-to-left shows the next photo, left-to-right the previous one
        self.manualSlide(dx < 0 ? 1 : -1);
      }
    };
    this.root.addEventListener('pointerup', function (event) { finish(event, false); });
    this.root.addEventListener('pointercancel', function (event) { finish(event, true); });

    this.root.addEventListener('contextmenu', function (event) { event.preventDefault(); });

    // Close: go back to the page the customer came from on this site, else to the link set in the editor
    var close = this.root.querySelector('[data-close]');
    if (close) {
      close.addEventListener('click', function (event) {
        var sameSite = document.referrer && document.referrer.indexOf(location.origin) === 0;
        if (sameSite && history.length > 1) {
          event.preventDefault();
          history.back();
        }
      });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown') self.goProduct(1);
      else if (event.key === 'ArrowUp') self.goProduct(-1);
      else if (event.key === 'ArrowRight') self.manualSlide(1);
      else if (event.key === 'ArrowLeft') self.manualSlide(-1);
    });

    var wheelLock = false;
    this.root.addEventListener('wheel', function (event) {
      if (wheelLock || Math.abs(event.deltaY) < 20) return;
      wheelLock = true;
      self.goProduct(event.deltaY > 0 ? 1 : -1);
      setTimeout(function () { wheelLock = false; }, 600);
    }, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.pause();
      else self.resume();
    });
  };

  /* ---------- app buttons (Growave rewards, Shopify chat) in the bottom bar ---------- */

  Reels.prototype.bindApps = function () {
    // App launchers (Growave rewards, Shopify chat) live in shadow DOMs and load late (the chat
    // even swaps a first placeholder button for the real one): every shadow root that holds a
    // launcher gets a style that hides the floating button; the bar's buttons click the current
    // one (a tap before the app is ready waits for it)
    var holders = function (selector, root, found) {
      found = found || [];
      if (root.querySelector(selector)) found.push(root);
      root.querySelectorAll('*').forEach(function (host) {
        if (host.shadowRoot) holders(selector, host.shadowRoot, found);
      });
      return found;
    };
    // The chat's panel (inside its runtime's shadow root): visible when the conversation is open
    var chatShowing = function () {
      var host = document.querySelector('shopify-chat');
      var runtime = host && host.shadowRoot && host.shadowRoot.querySelector('shopify-agent-runtime');
      var panel = runtime && runtime.shadowRoot && runtime.shadowRoot.querySelector('.panel');
      return !!panel && panel.getBoundingClientRect().height > 0;
    };
    var apps = [
      { button: this.root.querySelector('[data-nav-rewards]'), root: null, launcher: '.gw-rd-popup-widget-launcher button', hide: '.gw-rd-popup-widget-launcher' },
      { button: this.root.querySelector('[data-nav-chat]'), root: 'shopify-chat', launcher: 'button.activator', hide: '.activator, .activator-nudge', showing: chatShowing }
    ];
    var scopeOf = function (app) {
      if (!app.root) return document;
      var host = document.querySelector(app.root);
      return host && host.shadowRoot;
    };
    var currentLauncher = function (app) {
      var scope = scopeOf(app);
      var found = scope ? holders(app.launcher, scope) : [];
      // The last one found is the most recent (the real launcher, not the placeholder)
      return found.length ? found[found.length - 1].querySelector(app.launcher) : null;
    };
    apps.forEach(function (app) {
      if (!app.button) return;
      app.button.addEventListener('click', function () {
        // Opening the conversation reads it: the unread mark goes at once
        app.dismissed = true;
        var read = /^\((\d+)\)\s/.exec(document.title);
        app.seenCount = read ? parseInt(read[1], 10) : 0;
        app.button.classList.remove('has-unread');
        app.button.removeAttribute('data-unread');
        var attempts = 0;
        var open = function () {
          // The chat's launcher is a toggle and the chat remembers being open from the last page
          // (or is still starting): only open it, and check that it shows, trying again if not
          if (app.showing && app.showing()) return;
          var launcher = currentLauncher(app);
          if (launcher) {
            launcher.click();
            if (app.showing && ++attempts < 8) setTimeout(open, 900);
          } else if (++attempts < 40) {
            setTimeout(open, 300);
          }
        };
        open();
      });
    });
    var checks = 0;
    var hideLaunchers = function () {
      // After the first minute only the chat is checked (it's small): Growave's is in place by then
      var late = checks >= 60;
      apps.forEach(function (app) {
        if (late && !app.root) return;
        var scope = app.button && scopeOf(app);
        if (!scope) return;
        holders(app.launcher, scope).forEach(function (holder) {
          var target = holder === document ? document.head : holder;
          if (!target.querySelector('style[data-reels-hide="' + app.root + '"]')) {
            var style = document.createElement('style');
            style.setAttribute('data-reels-hide', String(app.root));
            style.textContent = app.hide + ' { opacity: 0 !important; pointer-events: none !important; }';
            target.appendChild(style);
          }
          // Its floating button is hidden: the app itself (popup, chat window) may show again
          document.documentElement.classList.add(app.root ? 'reels-chat-ready' : 'reels-gw-ready');
        });
      });
      // The rewards popup must sit above sticky headers, or its close button is hidden under them;
      // Growave's own rule wins over a stylesheet, so set it inline
      document.querySelectorAll('[class*="gw-widget-placeholder"][class$="-wrapper"]').forEach(function (wrapper) {
        wrapper.style.setProperty('z-index', '10000', 'important');
      });
      // Unread replies on the bar's chat button: the number the chat puts in the tab title ("(2) …"),
      // or a dot when only its own (hidden) bubble says "Respostas não lidas". Once she opens the
      // conversation it goes, and only comes back with a new reply
      apps.forEach(function (app) {
        if (!app.root || !app.button) return;
        var launcher = currentLauncher(app);
        var match = /^\((\d+)\)\s/.exec(document.title);
        var count = match ? parseInt(match[1], 10) : 0;
        var flagged = !!launcher && !!launcher.querySelector('.activator__indicator--active');
        if (!count) app.seenCount = 0;
        if (!flagged && !count) app.dismissed = false;
        if (app.showing && app.showing()) {
          app.dismissed = true;
          app.seenCount = count;
        }
        // A reply after she read the others: show again
        if (count > (app.seenCount || 0)) app.dismissed = false;
        var show = (count > 0 || flagged) && !app.dismissed;
        app.button.classList.toggle('has-unread', show);
        if (show && count) app.button.setAttribute('data-unread', count > 9 ? '9+' : String(count));
        else app.button.removeAttribute('data-unread');
      });
      // The chat can draw its bubble again much later (a conversation restored, a reply arriving),
      // so it keeps being checked, slowly, for as long as the page is open
      checks += 1;
      setTimeout(hideLaunchers, checks < 24 ? 250 : checks < 60 ? 1000 : 2000);
    };
    hideLaunchers();
  };

  /* ---------- navigation ---------- */

  Reels.prototype.bindNav = function () {
    var self = this;
    var root = this.root;
    this.bindApps();
    var menu = root.querySelector('[data-menu]');
    var search = root.querySelector('[data-search]');
    var bag = this.bag = root.querySelector('[data-bag]');
    var menuButton = root.querySelector('[data-nav-menu]');
    var searchButton = root.querySelector('[data-nav-search]');

    var anyOpen = function () {
      return (menu && !menu.hidden) || (search && !search.hidden) || (bag && !bag.hidden);
    };
    var closePanels = function () {
      var wasOpen = anyOpen();
      if (menu) menu.hidden = true;
      if (search) search.hidden = true;
      if (bag) bag.hidden = true;
      if (self.cartLink) self.cartLink.classList.remove('is-active');
      if (menuButton) menuButton.setAttribute('aria-expanded', 'false');
      if (searchButton) searchButton.classList.remove('is-active');
      if (wasOpen && self.current) self.resume();
    };
    this.closePanels = closePanels;

    var openPanel = function (panel) {
      var opening = panel.hidden;
      closePanels();
      if (!opening) return;
      panel.hidden = false;
      if (self.current) self.pause();
    };

    var homeButton = root.querySelector('[data-nav-home]');
    // "Casa": the reel fades, then the home page (without header and footer) opens with its
    // logo intro; its collections open back here as reels (snippets/reels-nav-bar.liquid)
    if (homeButton) homeButton.addEventListener('click', function () {
      closePanels();
      if (self.current) self.pause();
      root.classList.add('is-leaving');
      setTimeout(function () { window.location.href = root.dataset.homePage || '/'; }, 250);
    });
    // Coming back (browser cache): the reel again
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted || !root.classList.contains('is-leaving')) return;
      root.classList.remove('is-leaving');
      if (self.current) self.resume();
    });

    if (menuButton && menu) menuButton.addEventListener('click', function () {
      openPanel(menu);
      menuButton.setAttribute('aria-expanded', String(!menu.hidden));
    });

    // The bag opens its drawer (the cart page stays as the link's fallback)
    if (self.cartLink && bag) self.cartLink.addEventListener('click', function (event) {
      event.preventDefault();
      openPanel(bag);
      self.cartLink.classList.toggle('is-active', !bag.hidden);
      if (!bag.hidden) self.openBag();
    });
    // Close button and discount code field (assets/reels-bag.js)
    if (bag) self.bindBag(bag, closePanels);

    // The space the keyboard leaves: the search (at the top) keeps its results above it
    if (window.visualViewport) {
      var fitViewport = function () {
        root.style.setProperty('--reels-vvh', window.visualViewport.height + 'px');
      };
      window.visualViewport.addEventListener('resize', fitViewport);
      fitViewport();
    }

    if (searchButton && search) searchButton.addEventListener('click', function () {
      openPanel(search);
      searchButton.classList.toggle('is-active', !search.hidden);
      if (!search.hidden) search.querySelector('input[name="q"]').focus();
    });

    if (menu) {
      // Tabs: one menu at a time
      menu.querySelectorAll('[data-menu-tab]').forEach(function (tab) {
        tab.addEventListener('click', function () {
          menu.querySelectorAll('[data-menu-tab]').forEach(function (other) {
            var on = other === tab;
            other.classList.toggle('is-active', on);
            other.setAttribute('aria-selected', String(on));
          });
          menu.querySelectorAll('[data-menu-pane]').forEach(function (pane) {
            pane.hidden = pane.getAttribute('data-menu-pane') !== tab.getAttribute('data-menu-tab');
          });
          menu.scrollTop = 0;
          tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
        });
      });
      var tabRow = menu.querySelector('.reels__menu-tabs');
      if (tabRow) tabRow.addEventListener('scroll', function () {
        tabRow.classList.toggle('is-end', tabRow.scrollLeft + tabRow.clientWidth >= tabRow.scrollWidth - 2);
      }, { passive: true });
      menu.querySelectorAll('[data-menu-toggle]').forEach(function (toggle) {
        toggle.addEventListener('click', function () {
          var group = toggle.parentElement;
          var open = !group.classList.contains('is-open');
          group.classList.toggle('is-open', open);
          toggle.setAttribute('aria-expanded', String(open));
        });
      });
      // Collection links load in place as a new reel; other links (pages...) open normally
      menu.addEventListener('click', function (event) {
        var link = event.target.closest('a[href]');
        if (!link) return;
        var url = new URL(link.getAttribute('href'), window.location.href);
        if (url.origin !== window.location.origin) return;
        event.preventDefault();
        menu.querySelectorAll('.is-current').forEach(function (item) { item.classList.remove('is-current'); });
        link.classList.add('is-current');
        closePanels();
        if (isCollectionPath(url.pathname)) {
          self.closePage();
          self.loadSource(url.pathname);
        } else {
          self.openPage(url.pathname + url.search);
        }
      });
    }

    if (search) search.addEventListener('submit', function (event) {
      event.preventDefault();
      var query = search.querySelector('input[name="q"]').value.trim();
      if (!query) return;
      search.querySelector('input[name="q"]').blur();
      self.searchQuery = query;
      if (window.ReelsTrack) window.ReelsTrack.push({
        google: ['search', { search_term: query }],
        meta: ['Search', { search_string: query }]
      });
      closePanels();
      self.closePage();
      self.loadSource(search.getAttribute('action') + '?type=product&q=' + encodeURIComponent(query), true);
    });

    // Live results while typing (Shopify predictive search), below the field
    var results = search && search.querySelector('[data-results]');
    var searchInput = search && search.querySelector('input[name="q"]');
    var searchTimer = null;
    var searchToken = 0;
    var renderResults = function (query, products) {
      results.textContent = '';
      if (!query) {
        results.hidden = true;
        return;
      }
      results.hidden = false;
      if (!products.length) {
        results.appendChild(el('p', 'reels__results-empty', root.dataset.noResultsText || ''));
        return;
      }
      products.forEach(function (product) {
        var item = el('a', 'reels__result');
        item.href = product.url;
        var image = product.featured_image && product.featured_image.url || product.image;
        if (image) {
          var img = el('img');
          img.src = image + (image.indexOf('?') === -1 ? '?' : '&') + 'width=120';
          img.alt = '';
          img.loading = 'lazy';
          item.appendChild(img);
        }
        item.appendChild(el('span', 'reels__result-title', product.title));
        var cents = Math.round(parseFloat(product.price) * 100);
        if (!isNaN(cents)) item.appendChild(el('span', 'reels__result-price', self.formatMoney(cents)));
        item.addEventListener('click', function (event) {
          event.preventDefault();
          closePanels();
          self.openPage(product.url);
        });
        results.appendChild(item);
      });
      var all = el('button', 'reels__results-all', root.dataset.seeAllText || '');
      all.type = 'submit';
      results.appendChild(all);
    };
    if (searchInput && results) searchInput.addEventListener('input', function () {
      clearTimeout(searchTimer);
      var query = searchInput.value.trim();
      if (query.length < 2) {
        renderResults('', []);
        return;
      }
      searchTimer = setTimeout(function () {
        var token = ++searchToken;
        var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        fetch(base + 'search/suggest.json?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last')
          .then(function (response) { return response.json(); })
          .then(function (data) {
            if (token !== searchToken) return;
            var products = (data.resources && data.resources.results && data.resources.results.products) || [];
            renderResults(query, products);
          })
          .catch(function () {});
      }, 200);
    });

    // Reels mode: the theme keeps this bottom bar on the pages opened from here (see
    // snippets/reels-nav-bar.liquid). The value is the time of the last activity: the mode
    // expires after a few minutes without it, and the reel's close button ends it at once.
    var touchMode = function () {
      try { sessionStorage.setItem('reels-mode', String(Date.now())); } catch (e) {}
    };
    touchMode();
    root.addEventListener('pointerdown', touchMode, { passive: true });
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) touchMode();
    });
    var closeButton = root.querySelector('[data-close]');
    if (closeButton) closeButton.addEventListener('click', function () {
      try { sessionStorage.removeItem('reels-mode'); } catch (e) {}
    }, true);
    // Leave the New Experience: first "why?" (sent by email through the shop's contact form),
    // then the normal site again (no bar, header and footer back)
    var leaveReels = function (event) {
      if (window.ReelsTrack) {
        var picked = exitPanel && exitPanel.querySelector('[data-exit-reason]:checked');
        window.ReelsTrack.push({
          analyticsOnly: true,
          google: ['new_experience_exit', {
            reason: event && event.type === 'submit' && picked ? picked.value : 'sem motivo',
            transport_type: 'beacon'
          }]
        });
      }
      try {
        sessionStorage.removeItem('reels-mode');
        sessionStorage.removeItem('reels-account');
      } catch (e) {}
    };
    var exitLink = root.querySelector('[data-exit-reels]');
    var exitPanel = root.querySelector('[data-exit-panel]');
    if (exitLink && exitPanel) exitLink.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      closePanels();
      if (self.current) self.pause();
      exitPanel.hidden = false;
    }, true);
    if (exitPanel) {
      var exitSend = exitPanel.querySelector('[data-exit-send]');
      exitPanel.querySelectorAll('[data-exit-reason]').forEach(function (radio) {
        radio.addEventListener('change', function () { exitSend.disabled = false; });
      });
      var exitForm = exitPanel.querySelector('form');
      if (exitForm) exitForm.addEventListener('submit', leaveReels);
      var exitSkip = exitPanel.querySelector('[data-exit-skip]');
      if (exitSkip) exitSkip.addEventListener('click', leaveReels);
      // A tap above the sheet: stay
      exitPanel.addEventListener('click', function (event) {
        if (event.target !== exitPanel) return;
        exitPanel.hidden = true;
        if (self.current) self.resume();
      });
    }

    // Install as an app: Android's own prompt (when the browser offers it); on an iPhone, how to
    // "Ver o tutorial" in the menu
    var tutorialButton = root.querySelector('[data-tutorial-open]');
    if (tutorialButton) tutorialButton.addEventListener('click', function () {
      closePanels();
      self.maybeTutorial(true);
    });

    var installButton = root.querySelector('[data-install-app]');
    var installIos = root.querySelector('[data-install-ios]');
    var standalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
    var installPrompt = null;
    if (!standalone) {
      window.addEventListener('beforeinstallprompt', function (event) {
        event.preventDefault();
        installPrompt = event;
        if (installButton) installButton.hidden = false;
      });
      if (installIos && /iphone|ipad|ipod/i.test(window.navigator.userAgent)) installIos.hidden = false;
    }
    window.addEventListener('appinstalled', function () {
      if (window.ReelsTrack) window.ReelsTrack.push({ analyticsOnly: true, google: ['app_install', { method: 'android' }] });
    });
    if (installButton) installButton.addEventListener('click', function () {
      if (!installPrompt) return;
      installPrompt.prompt();
      installPrompt.userChoice.then(function (choice) {
        if (window.ReelsTrack) window.ReelsTrack.push({
          analyticsOnly: true,
          google: ['app_install_prompt', { outcome: (choice && choice.outcome) || '' }]
        });
        installPrompt = null;
        installButton.hidden = true;
      });
    });

    // Account (login / account pages on Shopify's own address): the site keeps the bar when
    // she comes back (snippets/reels-nav-bar.liquid)
    var accountLink = root.querySelector('.reels__nav-account');
    if (accountLink) accountLink.addEventListener('click', function () {
      try { sessionStorage.setItem('reels-account', String(Date.now())); } catch (e) {}
    });

    // Coming back from the site's bar: #c=/collections/x loads that collection, #search / #menu open them
    // The hash is marked as handled in the history state instead of being removed from the
    // address: changing the address makes Meta's pixel count a second page view
    var handled = history.state && history.state.reelsHashHandled;
    var hash = handled ? '' : decodeURIComponent(window.location.hash.slice(1));
    if (hash) {
      try { history.replaceState({ reelsHashHandled: true }, ''); } catch (e) {}
    }
    if (hash.indexOf('c=') === 0 && isCollectionPath(hash.slice(2))) {
      this.pendingSource = hash.slice(2);
    } else if (hash === 'search' && search) {
      openPanel(search);
      searchButton.classList.add('is-active');
      setTimeout(function () { search.querySelector('input[name="q"]').focus(); }, 50);
    } else if (hash === 'menu' && menu) {
      openPanel(menu);
      menuButton.setAttribute('aria-expanded', 'true');
    }

    // Tapping the photo area while a panel is open just closes it
    root.addEventListener('pointerdown', function (event) {
      if (event.target.closest('.reels__panel, .reels__nav, .reels__exit')) return;
      if (anyOpen()) closePanels();
    }, true);
  };

  // Pages and products open as normal pages: Shopify doesn't allow them inside a frame.
  // The bottom bar stays because the theme shows it on every page while in "reels mode"
  // (snippets/reels-nav-bar.liquid); collection links there come back here as a reel.
  Reels.prototype.openPage = function (url) {
    window.location.href = url;
  };

  Reels.prototype.closePage = function () {};

  // Re-reads the cart (after shopping inside an opened page)
  Reels.prototype.refreshCart = function () {
    var self = this;
    var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(base + 'cart.js')
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        if (self.pendingItems) return;
        self.cartItems = cart.item_count;
        self.cartCents = cart.total_price;
        self.renderCart(false);
      })
      .catch(function () {});
  };

  // Loads a collection or a search as the new reel, in place (Section Rendering API)
  Reels.prototype.loadSource = function (url, isSearch) {
    var self = this;
    if (!url) return;
    var loading = this.root.querySelector('[data-loading]');
    if (loading) loading.hidden = false;
    if (this.current) this.pause();
    this.loadToken = (this.loadToken || 0) + 1;
    var token = this.loadToken;
    // A reel on its way (the tutorial waits for it: it replaces every product on screen)
    this.loadingSource = true;

    // No "Accept: application/json" header here: on collection URLs it makes Shopify answer
    // with the collection's JSON instead of the rendered section
    fetch(url + (url.indexOf('?') === -1 ? '?' : '&') + 'sections=new-experience')
      .then(function (response) { return response.json(); })
      .then(function (sections) {
        if (token !== self.loadToken) return;
        var html = sections['new-experience'] || '';
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var script = doc.querySelector('[data-reels-products]');
        var products = self.prepareProducts(script ? JSON.parse(script.textContent) : []);
        var fetchedRoot = doc.querySelector('.reels');
        if (products.length && fetchedRoot) {
          self.sourceTitle = fetchedRoot.dataset.sourceTitle || '';
          self.sourceUrl = fetchedRoot.dataset.sourceUrl || '';
        }
        if (!products.length) {
          // Empty collection / no results: a friendly notice, the current reel stays
          self.showToast(isSearch ? self.root.dataset.noResultsText || '' : self.root.dataset.emptyCollectionText || '', false);
          if (self.current) self.resume();
          return;
        }
        // Replace the reel: drop every built product (and its video) and start again
        if (self.views) Object.keys(self.views).forEach(function (key) { self.destroyView(self.views[key]); });
        self.clearTimer();
        // With the tutorial open, the new reel stays still under it
        self.paused = !!self.holdForTutorial;
        self.products = products;
        if (isSearch) self.sourceUrl = '';
        self.start();
        self.trackList(isSearch, self.searchQuery);
        if (!self.bound) self.bind();
        self.prepareNeighbours();
      })
      .catch(function () {
        self.showToast(self.root.dataset.errorText || '', true);
        if (self.current) self.resume();
      })
      .then(function () {
        if (token !== self.loadToken) return;
        self.loadingSource = false;
        if (loading) loading.hidden = true;
      });
  };

  // The bag drawer (items, Peach Lovers, codes, total) is shared with the site's bottom bar
  if (window.ReelsBagMethods) {
    Object.keys(window.ReelsBagMethods).forEach(function (name) {
      Reels.prototype[name] = window.ReelsBagMethods[name];
    });
  }

  function init() {
    document.querySelectorAll('.reels').forEach(function (root) {
      if (!root._reels) root._reels = new Reels(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
